import type { JsonValue } from "@postdaily/api/lib/json";
import { redirect } from "@tanstack/react-router";
import { z } from "zod";

/**
 * The sign-up spellings this app has never had, and that people guess at.
 *
 * `/register` is the one with evidence: Search Console crawled it on 2 Aug and
 * again on 21 Aug and got a 404 both times. `/signup` and `/sign-up` are the
 * other two spellings of the same intent, added so the next guess lands
 * somewhere useful rather than waiting to show up in a report first.
 *
 * **They resolve to the sign-UP form, not the sign-in one.** `/login` renders
 * `SignInForm` by default and `SignUpForm` only at `?mode=signup`, and somebody
 * arriving at "register" wants to create an account. Dropping them on a
 * sign-in form is the kind of redirect that technically works and still loses
 * the visitor.
 *
 * **301, not the router's default 307.** These pages are permanently absent, so
 * a permanent redirect is what lets Google consolidate the URLs and stop
 * reporting them. A temporary one asks Google to keep checking forever.
 *
 * Three placement rules go with this, and each would break it quietly:
 *
 * 1. The routes live in `(public)`, never `(auth)`. `(auth)` is `ssr: false`,
 *    so `beforeLoad` would run in the browser and a crawler would get an empty
 *    shell instead of a redirect.
 * 2. They are excluded from `sitemap.xml` in `vite.config.ts`. Route discovery
 *    lists every route, and a redirect in a sitemap is the "Page with redirect"
 *    noise these exist to reduce.
 * 3. They stay ALLOWED in `robots.txt`, for the same reason `/login` is: a
 *    crawler has to be able to fetch the URL to see the 301.
 *
 * They must also stay out of the prerender filter. The prerenderer follows
 * redirects and writes the destination's HTML to the source path, which would
 * serve the login page at `/register` with a 200 — worse than the 404 it
 * replaced. `isPublicPrerenderPath` already returns false for all three.
 */
export function redirectToSignUp(): never {
  throw redirect({
    to: "/login",
    search: { mode: "signup" },
    statusCode: 301,
  });
}

/**
 * Returns a same-origin app path suitable for post-login navigation, or
 * undefined when the value is missing/unsafe (open-redirect guard).
 */
export function getSafeAuthRedirect(
  value: string | undefined,
): string | undefined {
  if (!value) {
    return undefined;
  }

  // Same-origin paths only: a protocol-relative `//evil.test` is a URL, not a
  // path, and is the classic open-redirect payload.
  // Browsers treat "\" in a URL path as "/", so "/\evil.test" is the same
  // protocol-relative payload in another spelling.
  if (
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\")
  ) {
    return undefined;
  }

  // Stay out of auth surfaces — bounce those to the default post-login page.
  if (
    value === "/login" ||
    value.startsWith("/login?") ||
    value === "/forgot-password" ||
    value.startsWith("/forgot-password?") ||
    value === "/reset-password" ||
    value.startsWith("/reset-password?") ||
    value === "/two-factor" ||
    value.startsWith("/two-factor?")
  ) {
    return undefined;
  }

  return value;
}

/** Which form `/login` shows. Sign-in is the default — see `authSearchSchema`. */
export type AuthMode = "signin" | "signup";

export function getAuthMode(value: string | undefined): AuthMode {
  return value === "signup" ? "signup" : "signin";
}

/**
 * `/login` search params: optional `?redirect=`, `?mode=signup` and `?error=`.
 *
 * The view lives in the URL rather than in a store so a "Sign in" link always
 * lands on the sign-in form — however the visitor got here, and whichever form
 * they last had open. Keys are omitted at their default so plain `/login` stays
 * search-free and a stale `?mode=signup` can't linger on the sign-in link.
 *
 * `error` is the code a failed OAuth callback redirects with (Better Auth's
 * `onAPIError.errorURL` points at this page); the sign-in form turns it into a
 * sentence. It is kept short and character-bounded so a hostile link cannot use
 * it to paint arbitrary text on our sign-in page.
 */
/**
 * The sanitized auth search params. Each key is ABSENT when it was not in the
 * URL — the router serializes that differently from an empty value, so the
 * distinction has to survive.
 */
export type AuthSearch = {
  redirect?: string;
  mode?: "signup";
  error?: string;
};

/**
 * The raw shape, before sanitizing. Parsed rather than duck-typed: this is the
 * URL, so every field is attacker-controlled. `error` is bounded to a short
 * code-shaped string precisely so a hostile link cannot paint arbitrary text
 * onto the sign-in page.
 */
const rawSearchSchema = z.object({
  error: z
    .string()
    .regex(/^[a-z_']{1,64}$/)
    .optional()
    .catch(undefined),
  mode: z.string().optional().catch(undefined),
  redirect: z.string().optional().catch(undefined),
});

/**
 * A URL search record as TanStack Router hands it over: keys parsed out of the
 * query string, each value still whatever `JSON.parse` made of it. This is the
 * boundary — `rawSearchSchema` below is what turns it into a domain value.
 */
export type RawAuthSearch = { [key: string]: JsonValue | undefined };

export function authSearchSchema(search: RawAuthSearch): AuthSearch {
  const raw = rawSearchSchema.safeParse(search);
  const fields = raw.success ? raw.data : {};
  const redirect = getSafeAuthRedirect(fields.redirect);
  const mode = getAuthMode(fields.mode);
  const error = fields.error;

  const parsed: AuthSearch = {};

  if (redirect) {
    parsed.redirect = redirect;
  }

  if (mode === "signup") {
    parsed.mode = mode;
  }

  if (error) {
    parsed.error = error;
  }

  return parsed;
}
